import type {
  RecordingRetentionMode,
  RecordingRetentionStatus,
  UpdateRecordingRetentionPayload,
} from './types';

const GIB = 1024 ** 3;

export type RecordingRetentionForm = {
  mode: RecordingRetentionMode;
  retentionDays: string;
  batchSize: string;
  runIntervalMinutes: string;
  warningUsedPercent: string;
  criticalUsedPercent: string;
  warningFreeGiB: string;
  criticalFreeGiB: string;
  confirmation: string;
};

export type RetentionFormResult = {
  payload: UpdateRecordingRetentionPayload | null;
  error: string;
};

function decimal(value: string) {
  const normalized = String(value || '').trim().replace(',', '.');
  return normalized === '' ? Number.NaN : Number(normalized);
}

function integerInRange(value: string, minimum: number, maximum: number, label: string) {
  const parsed = decimal(value);
  if (!Number.isInteger(parsed) || parsed < minimum || parsed > maximum) {
    throw new Error(`${label}: допустимо от ${minimum} до ${maximum}`);
  }
  return parsed;
}

function gibibytes(value: string, label: string) {
  const parsed = decimal(value);
  const bytes = Math.round(parsed * GIB);
  if (!Number.isFinite(parsed) || parsed < 0 || !Number.isSafeInteger(bytes)) {
    throw new Error(`${label}: укажите корректный объём`);
  }
  return bytes;
}

export function retentionFormPayload(
  form: RecordingRetentionForm,
  previousMode: RecordingRetentionMode,
): RetentionFormResult {
  try {
    const retentionDays = integerInRange(form.retentionDays, 30, 3650, 'Срок хранения');
    const batchSize = integerInRange(form.batchSize, 1, 500, 'Размер пачки');
    const runIntervalMinutes = integerInRange(form.runIntervalMinutes, 15, 1440, 'Интервал запуска');
    const warningUsedPercent = integerInRange(form.warningUsedPercent, 50, 95, 'Предупредительный процент');
    const criticalUsedPercent = integerInRange(form.criticalUsedPercent, 51, 99, 'Критический процент');
    const warningFreeBytes = gibibytes(form.warningFreeGiB, 'Предупредительный остаток');
    const criticalFreeBytes = gibibytes(form.criticalFreeGiB, 'Критический остаток');
    if (criticalUsedPercent <= warningUsedPercent) {
      return { payload: null, error: 'Критический процент должен быть выше предупредительного' };
    }
    if (criticalFreeBytes > warningFreeBytes) {
      return { payload: null, error: 'Критический остаток должен быть не больше предупредительного' };
    }
    if (previousMode !== 'ENABLED' && form.mode === 'ENABLED' && form.confirmation !== 'УДАЛЯТЬ') {
      return { payload: null, error: 'Для включения очистки введите УДАЛЯТЬ' };
    }
    const payload: UpdateRecordingRetentionPayload = {
      mode: form.mode,
      retentionDays,
      batchSize,
      runIntervalMinutes,
      warningUsedPercent,
      criticalUsedPercent,
      warningFreeBytes,
      criticalFreeBytes,
    };
    if (previousMode !== 'ENABLED' && form.mode === 'ENABLED') payload.confirmation = form.confirmation;
    return { payload, error: '' };
  } catch (error) {
    return { payload: null, error: error instanceof Error ? error.message : 'Проверьте настройки хранения' };
  }
}

function numberLabel(value: number) {
  const rounded = Math.round(value * 10) / 10;
  return (Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1)).replace('.', ',');
}

export function formatRecordingBytes(bytes: number) {
  const safe = Number.isFinite(bytes) ? Math.max(0, bytes) : 0;
  if (safe < 1024) return `${Math.round(safe)} Б`;
  if (safe < 1024 ** 2) return `${numberLabel(safe / 1024)} КиБ`;
  if (safe < GIB) return `${numberLabel(safe / 1024 ** 2)} МиБ`;
  if (safe < 1024 ** 4) return `${numberLabel(safe / GIB)} ГиБ`;
  return `${numberLabel(safe / 1024 ** 4)} ТиБ`;
}

function recordingCountLabel(value: number) {
  const absolute = Math.abs(value) % 100;
  const last = absolute % 10;
  if (absolute > 10 && absolute < 20) return 'записей';
  if (last === 1) return 'запись';
  if (last >= 2 && last <= 4) return 'записи';
  return 'записей';
}

export function retentionPresentation(status: RecordingRetentionStatus) {
  const storage = status.storage;
  const level = storage?.level || 'UNKNOWN';
  const levelPresentation = {
    OK: { label: 'Норма', tone: 'ok' as const },
    WARNING: { label: 'Требует внимания', tone: 'warning' as const },
    CRITICAL: { label: 'Критично', tone: 'critical' as const },
    UNKNOWN: { label: 'Нет данных', tone: 'unknown' as const },
  }[level];
  const candidates = Math.max(0, Number(status.recordings.candidateCount || 0));
  return {
    modeLabel: status.settings.mode === 'ENABLED' ? 'Очистка включена' : 'Только отчёт',
    modeTone: status.settings.mode === 'ENABLED' ? 'enabled' as const : 'report' as const,
    managedBytesLabel: formatRecordingBytes(status.recordings.managedBytes),
    usedPercentLabel: storage ? `${numberLabel(storage.usedPercent)} %` : 'Нет данных',
    freeBytesLabel: storage ? formatRecordingBytes(storage.availableBytes) : 'Нет данных',
    candidateLabel: `${candidates} ${recordingCountLabel(candidates)} · ${formatRecordingBytes(status.recordings.candidateBytes)}`,
    storageLevelLabel: levelPresentation.label,
    storageTone: levelPresentation.tone,
  };
}

export function retentionFormFromStatus(status: RecordingRetentionStatus): RecordingRetentionForm {
  const settings = status.settings;
  return {
    mode: settings.mode,
    retentionDays: String(settings.retentionDays),
    batchSize: String(settings.batchSize),
    runIntervalMinutes: String(settings.runIntervalMinutes),
    warningUsedPercent: String(settings.warningUsedPercent),
    criticalUsedPercent: String(settings.criticalUsedPercent),
    warningFreeGiB: numberLabel(settings.warningFreeBytes / GIB),
    criticalFreeGiB: numberLabel(settings.criticalFreeBytes / GIB),
    confirmation: '',
  };
}
